import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Check, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

export default function AdminChangeRequestReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [changeRequest, setChangeRequest] = useState(null);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [adminComment, setAdminComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchChangeRequest();
  }, [id]);

  const fetchChangeRequest = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/events/change-requests/${id}`, {
        headers: getAuthHeaders()
      });
      setChangeRequest(response.data.changeRequest);
      setCurrentEvent(response.data.currentEvent);
    } catch (error) {
      console.error('Failed to fetch change request:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else if (error.response?.status === 404) {
        toast.error('Change request not found');
        navigate('/admin/events/change-requests');
      } else {
        toast.error('Failed to load change request');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setSubmitting(true);
      await axios.post(
        `${API}/admin/events/change-requests/${id}/approve`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success('Change request approved', {
        description: 'Changes have been applied and the organiser has been notified.'
      });
      
      // Navigate back to list or to the event
      if (changeRequest.type === 'update') {
        navigate(`/admin/events/${changeRequest.eventId}`);
      } else {
        navigate('/admin/events/change-requests');
      }
    } catch (error) {
      console.error('Failed to approve:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Cannot approve this request');
      } else {
        toast.error('Failed to approve request');
      }
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!adminComment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        `${API}/admin/events/change-requests/${id}/reject`,
        { admin_comment: adminComment },
        { 
          headers: getAuthHeaders(),
          params: { admin_comment: adminComment }
        }
      );
      
      toast.success('Change request rejected', {
        description: 'The organiser has been notified with your feedback.'
      });
      
      setRejectDialogOpen(false);
      navigate('/admin/events/change-requests');
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject request');
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={variants[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldComparison = (label, currentValue, proposedValue) => {
    const hasChange = JSON.stringify(currentValue) !== JSON.stringify(proposedValue);
    
    return (
      <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-200">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{label}</p>
          <p className="text-sm text-gray-900">{currentValue || <span className="text-gray-400">Not set</span>}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{label} (Proposed)</p>
          <p className={`text-sm ${hasChange ? 'text-orange-700 font-semibold' : 'text-gray-900'}`}>
            {proposedValue || <span className="text-gray-400">Not set</span>}
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading change request...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!changeRequest || !currentEvent) {
    return null;
  }

  const isPending = changeRequest.status === 'pending';

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/events/change-requests')}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Review Change Request</h1>
              <p className="text-gray-600 mt-1">
                {changeRequest.type === 'update' ? 'Event Update' : 'New Edition'} • {getStatusBadge(changeRequest.status)}
              </p>
            </div>
          </div>

          {isPending && (
            <div className="flex space-x-2">
              <Button
                onClick={() => setRejectDialogOpen(true)}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="reject-button"
              >
                <X className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="approve-button"
              >
                <Check className="w-4 h-4 mr-2" />
                {submitting ? 'Approving...' : 'Approve'}
              </Button>
            </div>
          )}
        </div>

        {/* Request Info */}
        <Card>
          <CardHeader>
            <CardTitle>Request Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Event:</span>
                <p className="font-medium">{currentEvent.name}</p>
              </div>
              <div>
                <span className="text-gray-600">Submitted By:</span>
                <p className="font-medium">{changeRequest.submittedBy}</p>
              </div>
              <div>
                <span className="text-gray-600">Submitted At:</span>
                <p className="font-medium">{formatDate(changeRequest.submittedAt)}</p>
              </div>
            </div>
            {changeRequest.adminComment && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="text-sm font-medium text-amber-900 mb-1">Admin Comment:</p>
                <p className="text-sm text-amber-700">{changeRequest.adminComment}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Changes Comparison */}
        {changeRequest.type === 'update' ? (
          <Card>
            <CardHeader>
              <CardTitle>Proposed Changes</CardTitle>
              <CardDescription>Current values vs. proposed updates (highlighted in orange)</CardDescription>
            </CardHeader>
            <CardContent>
              {changeRequest.payload.name && renderFieldComparison('Event Name', currentEvent.name, changeRequest.payload.name)}
              {changeRequest.payload.city && renderFieldComparison('City', currentEvent.city, changeRequest.payload.city)}
              {changeRequest.payload.venueName && renderFieldComparison('Venue Name', currentEvent.venueName, changeRequest.payload.venueName)}
              
              {changeRequest.payload.dates && (
                <>
                  {renderFieldComparison('Start Date', currentEvent.dates?.start, changeRequest.payload.dates.start)}
                  {renderFieldComparison('End Date', currentEvent.dates?.end, changeRequest.payload.dates.end)}
                  {renderFieldComparison('Display Dates', currentEvent.dates?.display, changeRequest.payload.dates.display)}
                </>
              )}
              
              {changeRequest.payload.shortDescription && renderFieldComparison('Short Description', currentEvent.shortDescription, changeRequest.payload.shortDescription)}
              
              {changeRequest.payload.longDescription && (
                <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Long Description</p>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{currentEvent.longDescription}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Long Description (Proposed)</p>
                    <p className="text-sm text-orange-700 font-semibold whitespace-pre-wrap">{changeRequest.payload.longDescription}</p>
                  </div>
                </div>
              )}
              
              {changeRequest.payload.organiserWebsite && renderFieldComparison('Organiser Website', currentEvent.organiserWebsite, changeRequest.payload.organiserWebsite)}
              {changeRequest.payload.officialUrl && renderFieldComparison('Official URL', currentEvent.officialUrl, changeRequest.payload.officialUrl)}
              
              {changeRequest.payload.sectorTags && (
                <div className="grid grid-cols-2 gap-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Sector Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {currentEvent.sectorTags?.map((tag, i) => (
                        <Badge key={i} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Sector Tags (Proposed)</p>
                    <div className="flex flex-wrap gap-1">
                      {changeRequest.payload.sectorTags.map((tag, i) => (
                        <Badge key={i} className="bg-orange-100 text-orange-700">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>New Edition Details</CardTitle>
              <CardDescription>Proposed new event edition</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> Approving will create a new event page. The original event will remain unchanged.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">New Slug:</span>
                  <p className="font-medium">{changeRequest.payload.slug}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Event Name:</span>
                  <p className="font-medium">{changeRequest.payload.name}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Start Date:</span>
                  <p className="font-medium">{changeRequest.payload.dates?.start}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">End Date:</span>
                  <p className="font-medium">{changeRequest.payload.dates?.end}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Display:</span>
                  <p className="font-medium">{changeRequest.payload.dates?.display}</p>
                </div>
              </div>
              
              <div>
                <span className="text-sm text-gray-600">City & Venue:</span>
                <p className="font-medium">{changeRequest.payload.city} - {changeRequest.payload.venueName}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Change Request</DialogTitle>
              <DialogDescription>
                Provide a reason for rejection. This will be sent to the organiser.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="admin-comment">Reason for Rejection *</Label>
              <Textarea
                id="admin-comment"
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                rows={4}
                placeholder="e.g., The proposed dates conflict with another major event..."
                className="mt-2"
                data-testid="reject-comment-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleReject}
                disabled={submitting || !adminComment.trim()}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-reject-button"
              >
                {submitting ? 'Rejecting...' : 'Reject Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
